/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* @typescript-eslint/ban-ts-ignore */
import { saveAs } from 'file-saver';
import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'store';
import createCertificatesApi, { downloadFromUrl } from 'api/certificates';
import { showToast } from 'modules/app/toasterSlice';
import { currentUserSelector } from './userSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { setLoading } from 'modules/subscriptions/employeeBenefitSlice';
import JSZip from 'jszip';

interface State {
  loading: boolean;
  giftLoading: boolean;
  socialMediaLoading: boolean;
  instagramLoading: boolean;
  webLoading: boolean;
  posterLoading: boolean;
  totalImpact?: Certificate;
  giftedImpact?: Certificate;
  giftGiven?: Certificate;
  receivedGifts?: Certificate[];
  certificate?: Certificate;
}

const initialState: State = {
  loading: false,
  giftLoading: false,
  posterLoading: false,
  webLoading: false,
  socialMediaLoading: false,
  instagramLoading: false,
};

function removeEmptyProperties(obj: Record<string, unknown>) {
  return Object.entries(obj)
    .filter(([_, v]) => v != null)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
}

const slice = createSlice({
  name: 'certificates',
  initialState,
  reducers: {
    setTotalImpactCertificate(state: State, action: PayloadAction<Partial<Certificate>>): State {
      state.totalImpact = state.totalImpact ?? ({} as Certificate);
      state.totalImpact = { ...state.totalImpact, ...removeEmptyProperties(action.payload) };
      return state;
    },
    setGiftedImpactCertificate(state: State, action: PayloadAction<Partial<Certificate>>): State {
      state.giftedImpact = state.giftedImpact ?? ({} as Certificate);
      state.giftedImpact = { ...state.giftedImpact, ...removeEmptyProperties(action.payload) };
      return state;
    },
    addGiftedCertificate(state: State, action: PayloadAction<Partial<Certificate>>) {
      state.receivedGifts = state.receivedGifts ?? [];
      const existingGift = state.receivedGifts.find(gift => gift.giftCardId === action.payload.giftCardId);
      const existingGiftIndex = state.receivedGifts.findIndex(gift => gift.giftCardId === action.payload.giftCardId);

      if (existingGift && existingGiftIndex >= 0) {
        const newGift = { ...existingGift, ...removeEmptyProperties(action.payload) };
        state.receivedGifts[existingGiftIndex] = newGift;
        return state;
      }

      state.receivedGifts.push({ ...({} as Certificate), ...removeEmptyProperties(action.payload) });
      return state;
    },
    setGiftGivenCertificate(state: State, action: PayloadAction<Certificate>): State {
      state.giftGiven = action.payload;
      return state;
    },
    setLoading(state: State, action: PayloadAction<boolean>): State {
      state.loading = action.payload;
      return state;
    },
    setGiftCardLoading(state: State, action: PayloadAction<boolean>): State {
      state.giftLoading = action.payload;
      return state;
    },
    setSocialMediaLoading(state: State, action: PayloadAction<boolean>): State {
      state.socialMediaLoading = action.payload;
      return state;
    },
    setInstagramLoading(state: State, action: PayloadAction<boolean>): State {
      state.instagramLoading = action.payload;
      return state;
    },
    setPosterLoading(state: State, action: PayloadAction<boolean>): State {
      state.posterLoading = action.payload;
      return state;
    },
    setWebLoading(state: State, action: PayloadAction<boolean>): State {
      state.webLoading = action.payload;
      return state;
    },
    clearCertificates(state: State): State {
      state.totalImpact = undefined;
      state.giftedImpact = undefined;
      return state;
    },
    setCertificate(state: State, action: PayloadAction<Certificate>): State {
      state.certificate = action.payload;
      return state;
    },
  },
});

const {
  clearCertificates,
  setTotalImpactCertificate,
  setGiftedImpactCertificate,
  setSocialMediaLoading,
  setInstagramLoading,
  setPosterLoading,
  setWebLoading,
  setGiftCardLoading,
  addGiftedCertificate,
  setCertificate,
} = slice.actions;

export { clearCertificates };

export default slice.reducer;

export const prepareGiftCardForUser = (name = '', customerId = 'default', orderId: string, openInNewWindow = false): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const context = appContextSelector(getState());
  const certificatesApi = createCertificatesApi(context);
  dispatch(setGiftCardLoading(true));
  try {
    if (openInNewWindow) {
      const giftCard = await certificatesApi.printGiftCardForOrder(name, customerId, orderId);
      const fileURL = URL.createObjectURL(giftCard);
      window.open(fileURL)?.print();
    } else {
      const giftCard = await certificatesApi.getGiftCardForOrder(name, customerId, orderId);
      saveAs(giftCard, 'giftcard.pdf');
    }
    dispatch(setGiftCardLoading(false));
  } catch {
    dispatch(setGiftCardLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
  }
};

export const downloadCertificate = (certificateUrl: string, fileName: string, pdf?: boolean): AppThunk => async (dispatch): Promise<void> => {
  try {
    const blob = await downloadFromUrl(certificateUrl);
    saveAs(blob, `${fileName}.${pdf ? 'pdf' : 'png'}`);
  } catch {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const generateSocialMediaCertificates = (giftOrderId?: string): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const { totalImpact, giftedImpact, receivedGifts } = getState().certificates;
  const context = appContextSelector(getState());
  const variants: CertificateVariants[] = ['Facebook', 'Twitter'];
  const giftCard = receivedGifts?.find(card => card.giftCardId === giftOrderId);

  if (
    totalImpact?.facebookUrl &&
    totalImpact.twitterUrl &&
    giftedImpact?.twitterUrl &&
    giftedImpact.facebookUrl &&
    giftCard?.facebookUrl &&
    giftCard.twitterUrl
  ) {
    return;
  }
  dispatch(setSocialMediaLoading(true));
  try {
    await generateCertificate({
      dispatch,
      context,
      variants,
      customerId: currentAccount?.customerId,
      partnershipId: currentAccount?.partnershipId,
      userId: currentAccount?.id,
      giftOrderId,
    });
    dispatch(setSocialMediaLoading(false));
  } catch {
    dispatch(clearCertificates());
    dispatch(setSocialMediaLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
  }
};

export const generateWebCertificates = (giftOrderId?: string): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const { totalImpact, giftedImpact, receivedGifts } = getState().certificates;
  const context = appContextSelector(getState());
  const variants: CertificateVariants[] = ['Web', 'WebPrintFriendly'];
  const giftCard = receivedGifts?.find(card => card.giftCardId === giftOrderId);

  if (totalImpact?.webUrl && totalImpact.webPrintFriendlyUrl && giftedImpact?.webUrl && giftedImpact.webPrintFriendlyUrl) {
    return;
  }

  if (giftOrderId && giftCard?.webUrl && giftCard.webPrintFriendlyUrl) {
    return;
  }

  dispatch(setWebLoading(true));
  try {
    await generateCertificate({
      dispatch,
      context,
      variants,
      customerId: currentAccount?.customerId,
      partnershipId: currentAccount?.partnershipId,
      userId: currentAccount?.id,
      giftOrderId,
    });
    dispatch(setWebLoading(false));
  } catch {
    dispatch(clearCertificates());
    dispatch(setWebLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
  }
};

export const generateAndDownloadInstagramCertificate = (gifted: boolean, cb?: CallableFunction, giftOrderId?: string): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const { totalImpact, giftedImpact, receivedGifts } = getState().certificates;
  const context = appContextSelector(getState());
  const variants: CertificateVariants[] = ['Instagram'];
  const instagramFileName = 'instagram_certificate';

  const giftCard = receivedGifts?.find(card => card.giftCardId === giftOrderId);

  if (giftCard?.instagramUrl) {
    dispatch(downloadCertificate(giftCard.instagramUrl ?? '', instagramFileName));
  } else if (gifted && giftedImpact?.instagramUrl) {
    dispatch(downloadCertificate(giftedImpact.instagramUrl ?? '', instagramFileName));
    cb?.();
    return;
  } else if (!gifted && totalImpact?.instagramUrl) {
    dispatch(downloadCertificate(totalImpact.instagramUrl ?? '', instagramFileName));
    cb?.();
    return;
  }

  dispatch(setInstagramLoading(true));
  try {
    const certificate = await generateCertificate({
      dispatch,
      context,
      gifted,
      variants,
      customerId: currentAccount?.customerId,
      partnershipId: currentAccount?.partnershipId,
      userId: currentAccount?.id,
      giftOrderId,
    });
    dispatch(setInstagramLoading(false));
    dispatch(downloadCertificate(certificate?.instagramUrl ?? '', instagramFileName));
    cb?.();
  } catch {
    dispatch(clearCertificates());
    dispatch(setInstagramLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
  }
};

export const generateAndDownloadPosterCertificate = (gifted: boolean, cb?: CallableFunction, giftOrderId?: string): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const { totalImpact, giftedImpact, receivedGifts } = getState().certificates;
  const context = appContextSelector(getState());
  const variants: CertificateVariants[] = ['Poster'];
  const certificateFileName = 'certificate';

  const giftCard = receivedGifts?.find(card => card.giftCardId === giftOrderId);
  if (giftCard?.posterUrl) {
    dispatch(downloadCertificate(giftCard.posterUrl ?? '', certificateFileName, true));
  } else if (gifted && giftedImpact?.posterUrl) {
    dispatch(downloadCertificate(giftedImpact.posterUrl ?? '', certificateFileName, true));
    cb?.();
    return;
  } else if (!gifted && totalImpact?.posterUrl) {
    dispatch(downloadCertificate(totalImpact.posterUrl ?? '', certificateFileName, true));
    cb?.();
    return;
  }

  dispatch(setPosterLoading(true));
  try {
    const certificate = await generateCertificate({
      dispatch,
      context,
      gifted,
      variants,
      customerId: currentAccount?.customerId,
      partnershipId: currentAccount?.partnershipId,
      userId: currentAccount?.id,
      giftOrderId,
    });
    dispatch(setPosterLoading(false));
    dispatch(downloadCertificate(certificate?.posterUrl ?? '', certificateFileName, true));
    cb?.();
  } catch {
    dispatch(clearCertificates());
    dispatch(setPosterLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
  }
};

export const generateAndDownloadPrintFriendlyPosterCertificate = (gifted: boolean, giftOrderId?: string): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const { totalImpact, giftedImpact, receivedGifts } = getState().certificates;
  const context = appContextSelector(getState());
  const variants: CertificateVariants[] = ['PosterPrintFriendly'];
  const certificateFileName = 'certificate';

  const giftCard = receivedGifts?.find(card => card.giftCardId === giftOrderId);
  if (giftCard?.posterPrintFriendlyUrl) {
    dispatch(downloadCertificate(giftCard.posterPrintFriendlyUrl ?? '', certificateFileName, true));
    return;
  } else if (gifted && giftedImpact?.posterPrintFriendlyUrl) {
    dispatch(downloadCertificate(giftedImpact.posterPrintFriendlyUrl ?? '', certificateFileName, true));
    return;
  } else if (!gifted && totalImpact?.posterPrintFriendlyUrl) {
    dispatch(downloadCertificate(totalImpact.posterPrintFriendlyUrl ?? '', certificateFileName, true));
    return;
  }

  dispatch(setPosterLoading(true));
  try {
    const certificate = await generateCertificate({
      dispatch,
      context,
      gifted,
      variants,
      customerId: currentAccount?.customerId,
      partnershipId: currentAccount?.partnershipId,
      userId: currentAccount?.id,
      giftOrderId,
    });
    dispatch(setPosterLoading(false));
    dispatch(downloadCertificate(certificate?.posterPrintFriendlyUrl ?? '', certificateFileName, true));
  } catch {
    dispatch(clearCertificates());
    dispatch(setPosterLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
  }
};

export const generateAndPrintPosterCertificate = (gifted: boolean, giftOrderId?: string): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const { totalImpact, giftedImpact, receivedGifts } = getState().certificates;
  const context = appContextSelector(getState());
  const variants: CertificateVariants[] = ['PosterPrintFriendly'];

  function print(url: string): void {
    window.open(url)?.print();
  }

  const giftCard = receivedGifts?.find(card => card.giftCardId === giftOrderId);
  if (giftCard) {
    print(giftCard.posterPrintFriendlyUrl ?? '');
    return;
  } else if (gifted && giftedImpact?.posterPrintFriendlyUrl) {
    print(giftedImpact.posterPrintFriendlyUrl);
    return;
  } else if (!gifted && totalImpact?.posterPrintFriendlyUrl) {
    print(totalImpact.posterPrintFriendlyUrl);
    return;
  }

  dispatch(setPosterLoading(true));
  try {
    const certificate = await generateCertificate({
      dispatch,
      context,
      gifted,
      variants,
      customerId: currentAccount?.customerId,
      partnershipId: currentAccount?.partnershipId,
      userId: currentAccount?.id,
      giftOrderId,
    });
    dispatch(setPosterLoading(false));
    print(certificate?.posterPrintFriendlyUrl ?? '');
  } catch {
    dispatch(clearCertificates());
    dispatch(setPosterLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
  }
};

interface GenerateCertificateOptions {
  dispatch: Dispatch;
  context: PortalContext;
  userId?: string;
  customerId?: string;
  partnershipId?: string;
  variants: CertificateVariants[];
  gifted?: boolean;
  giftOrderId?: string;
}

async function generateCertificate(options: GenerateCertificateOptions): Promise<Certificate | null> {
  const { dispatch, context, variants, customerId, userId, partnershipId, giftOrderId, gifted = false } = options;
  const certificatesApi = createCertificatesApi(context);
  if (['guest-wechooose', 'guest-mychooose'].includes(context)) {
    const certificate = await certificatesApi.getCertificatesForCustomer({
      id: customerId ?? '',
      type: 'TotalImpact',
      variants,
      context: 'MyChooose',
    });
    dispatch(setTotalImpactCertificate(certificate));
    return certificate;
  } else if (context === 'wechooose') {
    const certificate = await certificatesApi.getCertificatesForCustomer({
      id: customerId ?? '',
      type: 'TotalImpact',
      variants,
    });
    dispatch(setTotalImpactCertificate(certificate));
    return certificate;
  } else if (context === 'mychooose') {
    const [totalImpactCertificate, giftedImpactCertificate] = await Promise.all([
      certificatesApi.getCertificatesForUser({ id: userId ?? '', type: 'TotalImpact', variants }),
      certificatesApi.getCertificatesForCustomer({ id: customerId ?? '', type: 'GiftedImpact', variants }),
    ]);

    const receivedGiftCertificate = giftOrderId
      ? await certificatesApi.getCertificatesOrderForCustomer({ id: customerId ?? '', orderId: giftOrderId, type: 'GiftOrderReceived', variants })
      : null;

    dispatch(setTotalImpactCertificate(totalImpactCertificate));
    dispatch(setGiftedImpactCertificate(giftedImpactCertificate));
    if (receivedGiftCertificate) {
      receivedGiftCertificate.giftCardId = giftOrderId;
      dispatch(addGiftedCertificate(receivedGiftCertificate));
    }
    if (giftOrderId) {
      return receivedGiftCertificate;
    }

    if (gifted) {
      return giftedImpactCertificate;
    }
    return totalImpactCertificate;
  } else if (context === 'connect') {
    const totalImpactCertificate = await certificatesApi.getCertificatesForPartnership({
      type: 'TotalImpact',
      id: partnershipId ?? '',
      variants,
    });
    dispatch(setTotalImpactCertificate(totalImpactCertificate));
    return totalImpactCertificate;
  }

  return null;
}

export const generateAndDownloadOrderCertificate = (orderId: string, download = false): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const certificatesApi = createCertificatesApi(context);

  dispatch(setPosterLoading(true));
  try {
    const certificate = await certificatesApi.getCertificatesOrderForCustomer({
      id: currentAccount?.customerId ?? '',
      orderId: orderId,
      type: 'Order',
      variants: ['PosterLandscape'],
      isGuestCertificatePreview: context.includes('guest'),
    });
    download ? dispatch(downloadCertificate(certificate.posterLandscapeUrl ?? '', `order-${orderId}`, true)) : dispatch(setCertificate(certificate));
  } catch {
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
  } finally {
    dispatch(setPosterLoading(false));
  }
};

export const downloadRegistryCertificates = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const viewedOrder = getState().orders.viewedOrder;
  const retirements = viewedOrder?.retirements;
  const folderName = 'registry-certificates';
  const zip = new JSZip();
  const certFolder = zip.folder(folderName);
  let index = 0;

  if (!retirements) {
    return;
  }
  for (const ret of retirements) {
    const filename = `certificate-${index + 1}.pdf`;
    dispatch(setPosterLoading(true));
    try {
      const file = await downloadFromUrl(ret.registryCertificateUrl);
      certFolder?.file(filename, file, { binary: true });

      index += 1;
    } catch {
      dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
    } finally {
      dispatch(setPosterLoading(false));
    }
  }

  if (index > 0) {
    try {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${folderName}.zip`);
    } catch {
      dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:impact.certificateError' }));
    }
  }
};
